import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { blogPosts as blogs } from '@/data/siteData';
import { 
  Calendar,
  Clock,
  User,
  ArrowRight,
  Search,
  Tag
} from 'lucide-react';

const categories = ['All', 'Cloud', 'Security', 'Digital', 'Infrastructure', 'Software', 'Workplace', 'Technology', 'Compliance'];

export const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredBlogs, setFilteredBlogs] = useState(blogs);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let filtered = blogs;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(blog => blog.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(blog => 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredBlogs(filtered);
  }, [searchTerm, selectedCategory]);

  return (
    <>
      <Helmet>
        <title>Blog - Felix Infotech | Technology Insights, News & Best Practices</title>
        <meta name="description" content="Stay updated with the latest technology trends, insights, and best practices from Felix Infotech. Expert articles on cloud, cybersecurity, digital transformation, and enterprise IT." />
        <meta name="keywords" content="Felix Infotech blog, IT technology blog, cybersecurity insights, cloud computing articles, digital transformation, enterprise IT trends India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/blog" />
        <meta property="og:type" content="blog" />
        <meta property="og:title" content="Technology Blog - Felix Infotech | Insights & News" />
        <meta property="og:description" content="Expert insights on cloud, cybersecurity, digital transformation, and enterprise IT." />
        <meta property="og:url" content="https://felixinfotech.com/blog" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech Technology Blog" />
        <meta name="twitter:description" content="Latest trends, insights, and best practices in enterprise IT." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Animated Hero Section */}
        <AnimatedPageHero
          title="Insights & Resources"
          subtitle="Technology Blog"
          description="Stay informed with the latest technology trends, best practices, and expert insights from our team."
          badge="Blog"
          animationType="blog"
          gradient="from-indigo-500 to-purple-600"
          breadcrumbs={[{ label: 'Blog' }]}
        >
          {/* Search Bar */}
          <div className="mt-8 max-w-md">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40"
              />
            </div>
          </div>
        </AnimatedPageHero>

        {/* Category Filter */}
        <section className="py-8 bg-muted sticky top-20 z-30">
          <div className="container mx-auto px-4 lg:px-6">
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card hover:bg-muted-foreground/10 border border-border'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-6">
            {/* Featured Blog */}
            {filteredBlogs.length > 0 && selectedCategory === 'All' && !searchTerm && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-16"
              >
                <Link to={`/blog/${filteredBlogs[0].id}`}>
                  <div className="group grid lg:grid-cols-2 gap-8 bg-card rounded-3xl overflow-hidden shadow-soft border border-border hover:shadow-elevated transition-all">
                    <div className="relative h-64 lg:h-auto">
                      <img
                        src={filteredBlogs[0].image}
                        alt={filteredBlogs[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-primary text-primary-foreground">Featured</Badge>
                      </div>
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4">
                        <Badge variant="outline">{filteredBlogs[0].category}</Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {filteredBlogs[0].readTime}
                        </span>
                      </div>
                      <h2 className="font-display text-2xl lg:text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                        {filteredBlogs[0].title}
                      </h2>
                      <p className="text-muted-foreground mb-6">
                        {filteredBlogs[0].excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{filteredBlogs[0].author}</p>
                            <p className="text-xs text-muted-foreground">{filteredBlogs[0].date}</p>
                          </div>
                        </div>
                        <span className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                          Read More <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )}

            {/* Blog Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {(selectedCategory === 'All' && !searchTerm ? filteredBlogs.slice(1) : filteredBlogs).map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${blog.id}`}>
                    <div className="group h-full bg-card rounded-2xl overflow-hidden shadow-soft border border-border hover:shadow-elevated hover:-translate-y-2 transition-all">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
                        <Badge className="absolute bottom-4 left-4 bg-white/90 text-foreground">
                          {blog.category}
                        </Badge>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {blog.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {blog.readTime}
                          </span>
                        </div>
                        <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {blog.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {blog.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
                          Read Article <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {filteredBlogs.length === 0 && (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg">No articles found matching your criteria.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        <ContactForm variant="simple" />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default BlogPage;
